import Grid from '@mui/material/Grid2';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Card from '@mui/material/Card';
import {
  ChangeEvent, useEffect, useState,
} from 'react';
import Stack from '@mui/material/Stack';
import startCase from 'lodash/startCase';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { MemoizedBaseAvatar, BaseSearch } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { ReactComponent as CheckIcon } from '../../static/ic_outline_check.svg';
import { BaseFilterMenu } from '../../../../components/base';
import { useTeamFilters } from '../../../team/hooks';
import { useFilter } from '../../../../hooks/useFilter';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getCommissionPlans, getMemberEmails } from '../../../../store/commissions/selectors';
import { commissionsActions } from '../../../../store/commissions/slice';
import { PlanPopover } from './PlanPopover';
import { MemoizedMultiLocations } from '../../../team/components/Table/MultiLocations';

const SelectMembers = () => {
  const { t } = useTranslation(['team', 'commissions', 'inputs']);
  const { getTeamByFilters } = useTeamFilters();
  const { teamFilterItems } = useTeamFilters();
  const { filterItems } = useFilter(teamFilterItems);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const dispatch = useDispatch();
  const commissionPlans = useAppSelector(getCommissionPlans);

  const [localMembers, setLocalMembers] = useState(getTeamByFilters);

  const memberEmails = useAppSelector(getMemberEmails);

  useEffect(() => {
    setLocalMembers(getTeamByFilters);
  }, [getTeamByFilters]);

  const addMember = (email: string) => {
    dispatch(commissionsActions.setMemberEmails([...memberEmails, email]));
  };

  const removeMember = (email: string) => {
    dispatch(commissionsActions.setMemberEmails(memberEmails.filter((memberEmail) => memberEmail !== email)));
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value?.toLowerCase();

    if (searchValue === '') {
      setLocalMembers(getTeamByFilters);
    } else {
      setLocalMembers(getTeamByFilters.filter((member) => member.name?.toLowerCase().includes(searchValue)));
    }
  };

  const selectAll = () => {
    dispatch(commissionsActions.setMemberEmails(getTeamByFilters.map((member) => member.email)));
  };

  const removeAll = () => {
    dispatch(commissionsActions.setMemberEmails([]));
  };

  const getIsCommissionPlanExists = (id: number) => commissionPlans.find((plan) => plan.id === id);

  return (
    <Box pt={isMobile ? 3 : 6} pb={8}>
      <Grid mb={2} container spacing={4}>
        <Grid
          size={{
            lg: 7,
          }}
        >
          <Stack direction="row" spacing={1}>
            <Button onClick={selectAll}>{t('selectAll')}</Button>
            <Button onClick={removeAll} color="inherit">{t('removeAll')}</Button>
            <BaseFilterMenu items={filterItems || []} />
          </Stack>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          size={{
            lg: 5,
            xs: 12,
          }}
        >
          <BaseSearch placeholder={t('search', { ns: 'inputs' })} onChange={handleSearch} fullWidth />
        </Grid>
      </Grid>
      {localMembers.length === 0 ? (
        <CustomNoRowsOverlay text={t('noMembersFound', { ns: 'commissions' })} />
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {localMembers.map((member) => (
            <Grid
              key={member.email}
              size={{
                lg: 4,
                xs: 12,
                md: 6,
                sm: 6,
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  border: memberEmails.includes(member.email) ? '2px solid #A855F7' : 'none',
                  cursor: 'pointer',
                }}
                onClick={() => (memberEmails.includes(member.email) ? removeMember(member.email) : addMember(member.email))}
              >
                <CardContent>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <ListItem
                      sx={{
                        px: 0,
                      }}
                      alignItems="flex-start"
                      secondaryAction={(
                        <IconButton>
                          {memberEmails.includes(member.email) ? (
                            <SvgIcon
                              sx={{
                                fill: 'none',
                                color: '#A855F7',
                              }}
                              component={CheckIcon}
                              inheritViewBox
                            />
                          ) : (
                            <RadioButtonUncheckedIcon color="primary" />
                          )}
                        </IconButton>
                      )}
                    >
                      <MemoizedBaseAvatar avatarName={member.name} avatarFile={member.avatarFile} />
                      <ListItemText
                        primary={startCase(member.name)}
                        secondary={(
                          <Typography color="primary" variant="subtitle2" component="div">
                            {startCase(member.permissionLevel)}
                          </Typography>
                        )}
                      />
                    </ListItem>
                  </Grid>
                  <Stack px={1}>
                    <Stack spacing={1} direction="row">
                      <Typography
                        pt={1}
                        variant="subtitle2"
                        sx={{
                          fontFamily: 600,
                        }}
                      >
                        {`${t('locations', { ns: 'inputs' })}:`}
                      </Typography>
                      <Typography pt={1} variant="body2" color="text.secondary">
                        <MemoizedMultiLocations locations={member.locations} />
                      </Typography>
                    </Stack>
                    {member.commissionPlan && getIsCommissionPlanExists(member.commissionPlan.id) && (
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1}>
                        <Typography
                          pt={1}
                          variant="subtitle2"
                        >
                          {`${t('type', { ns: 'inputs' })}:`}
                        </Typography>
                        {member.commissionPlan && getIsCommissionPlanExists(member.commissionPlan.id) && (
                        <Box pt={1}>
                          <PlanPopover planId={member.commissionPlan.id} />
                        </Box>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography
                          variant="subtitle2"
                        >
                          {t('currentCommissionPlan', { ns: 'commissions' })}
                          :
                        </Typography>
                        <Typography variant="body2">{member.commissionPlan.name}</Typography>
                      </Stack>
                    </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export {
  SelectMembers,
};
